import React, { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';

function EditSampleModal({ sample, onClose, onSave }) {
  const [growers, setGrowers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [fields, setFields] = useState([]);

  const [selectedGrower, setSelectedGrower] = useState(sample.grower_id);
  const [selectedFarm, setSelectedFarm] = useState(sample.farm_id);
  const [selectedField, setSelectedField] = useState(sample.field_id);

  const [sampleName, setSampleName] = useState(sample.sample_name || '');
  const [zone, setZone] = useState(sample.zone || '');
  const [plotId, setPlotId] = useState(sample.plot_id || '');
  const [crop, setCrop] = useState(sample.crop || '');
  const [yieldGoal, setYieldGoal] = useState(sample.yield_goal || '');
  const [previousCrop, setPreviousCrop] = useState(sample.previous_crop || '');
  const [previousCropYield, setPreviousCropYield] = useState(sample.previous_crop_yield || '');
  const [limeHistory, setLimeHistory] = useState(
    sample.lime_history || []
  );
  const [programLevel, setProgramLevel] = useState(sample.program_level || 'Excellent');
  const [organic, setOrganic] = useState(sample.organic || false);
  const [specialNotes, setSpecialNotes] = useState(sample.special_notes || '');
  const [duplicatePlotId, setDuplicatePlotId] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Test selections
  const [tests, setTests] = useState({
    test_b: sample.test_b ?? true,
    test_ca: sample.test_ca ?? true,
    test_cu: sample.test_cu ?? true,
    test_fe: sample.test_fe ?? true,
    test_k: sample.test_k ?? true,
    test_mg: sample.test_mg ?? true,
    test_mn: sample.test_mn ?? true,
    test_na: sample.test_na ?? true,
    test_om: sample.test_om ?? true,
    test_p2: sample.test_p2 ?? true,
    test_ph1: sample.test_ph1 ?? true,
    test_s: sample.test_s ?? true,
    test_zn: sample.test_zn ?? true,
    test_cl: sample.test_cl ?? false,
    test_co: sample.test_co ?? false,
    test_mo: sample.test_mo ?? false,
    test_salts: sample.test_salts ?? false,
    test_bulk_den: sample.test_bulk_den ?? false,
    test_olsen: sample.test_olsen ?? false,
    test_nh3: sample.test_nh3 ?? false,
    test_no3: sample.test_no3 ?? false,
    test_ssc: sample.test_ssc ?? false
  });

  useEffect(() => {
    fetchGrowers();
  }, []);

  useEffect(() => {
    if (selectedGrower) {
      fetchFarms(selectedGrower);
    }
  }, [selectedGrower]);

  useEffect(() => {
    if (selectedFarm) {
      fetchFields(selectedFarm);
    }
  }, [selectedFarm]);

  // Check for duplicate PlotIDs
  useEffect(() => {
    const checkDuplicatePlotId = async () => {
      const trimmedPlotId = plotId?.trim();
      if (!trimmedPlotId || trimmedPlotId.length < 3) {
        setDuplicatePlotId(null);
        setShowDuplicateWarning(false);
        return;
      }

      try {
        const response = await authFetch(`${API_BASE}/check-plotid/${trimmedPlotId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.exists && data.samples && data.samples.length > 0) {
            // Filter out the current sample being edited to avoid self-warning
            const otherSamples = data.samples.filter(s => s.id !== sample.id);
            if (otherSamples.length > 0) {
              setDuplicatePlotId({ ...data, samples: otherSamples, count: otherSamples.length });
              setShowDuplicateWarning(true);
            } else {
              setDuplicatePlotId(null);
              setShowDuplicateWarning(false);
            }
          } else {
            setDuplicatePlotId(null);
            setShowDuplicateWarning(false);
          }
        }
      } catch (err) {
        console.error('Failed to check duplicate PlotID:', err);
      }
    };

    const timer = setTimeout(checkDuplicatePlotId, 800);
    return () => clearTimeout(timer);
  }, [plotId, sample.id]);

  const fetchGrowers = async () => {
    try {
      const companiesRes = await authFetch(`${API_BASE}/companies/`);
      const companies = await companiesRes.json();

      let allGrowers = [];
      for (const company of companies) {
        const growersRes = await authFetch(`${API_BASE}/growers/company/${company.id}`);
        const companyGrowers = await growersRes.json();
        allGrowers = [...allGrowers, ...companyGrowers];
      }
      setGrowers(allGrowers);
    } catch (err) {
      console.error('Failed to fetch growers:', err);
    }
  };

  const fetchFarms = async (growerId) => {
    try {
      const res = await authFetch(`${API_BASE}/farms/grower/${growerId}`);
      const data = await res.json();
      setFarms(data);
    } catch (err) {
      console.error('Failed to fetch farms:', err);
    }
  };

  const fetchFields = async (farmId) => {
    try {
      const res = await authFetch(`${API_BASE}/fields/farm/${farmId}`);
      const data = await res.json();
      setFields(data);
    } catch (err) {
      console.error('Failed to fetch fields:', err);
    }
  };

  const handleGrowerChange = (e) => {
    const growerId = parseInt(e.target.value, 10);
    setSelectedGrower(growerId);
    setSelectedFarm(null);
    setSelectedField(null);
    setFarms([]);
    setFields([]);
  };

  const handleFarmChange = (e) => {
    const farmId = parseInt(e.target.value, 10);
    setSelectedFarm(farmId);
    setSelectedField(null);
    setFields([]);
  };

  const handleFieldChange = (e) => {
    const fieldId = parseInt(e.target.value, 10);
    setSelectedField(fieldId);
  };

  const addLimeEntry = () => {
    setLimeHistory([
      ...limeHistory,
      { type: 'Calcium Carbonate', month: 1, year: new Date().getFullYear(), amount_lbs_ac: 0 }
    ]);
  };

  const updateLimeEntry = (index, field, value) => {
    const updated = [...limeHistory];
    updated[index][field] = value;
    setLimeHistory(updated);
  };

  const removeLimeEntry = (index) => {
    setLimeHistory(limeHistory.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedGrower || !selectedFarm || !selectedField) {
      alert('Please select Grower, Farm, and Field');
      return;
    }

    try {
      const payload = {
        grower_id: selectedGrower,
        farm_id: selectedFarm,
        field_id: selectedField,
        sample_name: sampleName,
        zone: zone,
        plot_id: plotId,
        crop: crop,
        yield_goal: yieldGoal ? parseFloat(yieldGoal) : null,
        previous_crop: previousCrop,
        previous_crop_yield: previousCropYield ? parseFloat(previousCropYield) : null,
        lime_history: limeHistory.length > 0 ? limeHistory : null,
        program_level: programLevel,
        organic: organic,
        special_notes: specialNotes,
        tests: tests
      };

      const res = await authFetch(`${API_BASE}/samples/${sample.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert('Sample updated successfully!');
        onSave();
      } else {
        const error = await res.json();
        alert(`Failed to update sample: ${error.detail || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const toggleTest = (testName) => {
    setTests((prev) => ({ ...prev, [testName]: !prev[testName] }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Edit Sample</h2>
            <p className="text-sm text-gray-500">Sample {sample.sample_sequence} - Bag ID: {sample.bag_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Hierarchy Selection */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grower *</label>
                <select
                  value={selectedGrower || ''}
                  onChange={handleGrowerChange}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select Grower</option>
                  {growers.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.grower_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Farm *</label>
                <select
                  value={selectedFarm || ''}
                  onChange={handleFarmChange}
                  className="w-full border rounded-lg px-3 py-2"
                  disabled={!selectedGrower}
                >
                  <option value="">Select Farm</option>
                  {farms.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.farm_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field *</label>
                <select
                  value={selectedField || ''}
                  onChange={handleFieldChange}
                  className="w-full border rounded-lg px-3 py-2"
                  disabled={!selectedFarm}
                >
                  <option value="">Select Field</option>
                  {fields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.field_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sample Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sample Name</label>
                <input
                  type="text"
                  value={sampleName}
                  onChange={(e) => setSampleName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Optional sample name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Zone</label>
                <input
                  type="text"
                  value={zone}
                  onChange={(e) => setZone(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., North, South"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Plot ID</label>
                <input
                  type="text"
                  value={plotId}
                  onChange={(e) => setPlotId(e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${showDuplicateWarning ? 'border-yellow-500' : ''}`}
                  placeholder="Plot tracking ID"
                />
                {showDuplicateWarning && duplicatePlotId && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-600 font-bold text-lg">⚠️</span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-yellow-800">
                          Warning: PlotID "{plotId}" already exists!
                        </p>
                        <p className="text-xs text-yellow-700 mt-1">
                          This PlotID has been used in {duplicatePlotId.count} other sample(s). 
                          PlotIDs should be unique field location identifiers.
                        </p>
                        <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded border border-yellow-200">
                          <p className="font-semibold mb-1">Most recent usage:</p>
                          <p><strong>Batch:</strong> {duplicatePlotId.samples[0].batch_id}</p>
                          {duplicatePlotId.samples[0].company_name && (
                            <p><strong>Company:</strong> {duplicatePlotId.samples[0].company_name}</p>
                          )}
                          {duplicatePlotId.samples[0].grower_name && (
                            <p><strong>Grower:</strong> {duplicatePlotId.samples[0].grower_name}</p>
                          )}
                          {duplicatePlotId.samples[0].farm_name && (
                            <p><strong>Farm:</strong> {duplicatePlotId.samples[0].farm_name}</p>
                          )}
                          {duplicatePlotId.samples[0].field_name && (
                            <p><strong>Field:</strong> {duplicatePlotId.samples[0].field_name}</p>
                          )}
                          {duplicatePlotId.samples[0].crop && (
                            <p><strong>Crop:</strong> {duplicatePlotId.samples[0].crop}</p>
                          )}
                          {duplicatePlotId.samples[0].created_at && (
                            <p><strong>Date:</strong> {new Date(duplicatePlotId.samples[0].created_at).toLocaleDateString()}</p>
                          )}
                        </div>
                        <p className="text-xs text-yellow-700 mt-2 font-medium">
                          Please verify this is correct or use a different PlotID.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Crop</label>
                <input
                  type="text"
                  value={crop}
                  onChange={(e) => setCrop(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., Corn, Soybeans"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yield Goal (units/ac)</label>
                <input
                  type="number"
                  value={yieldGoal}
                  onChange={(e) => setYieldGoal(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Target yield"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Previous Crop</label>
                <input
                  type="text"
                  value={previousCrop}
                  onChange={(e) => setPreviousCrop(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Last season's crop"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Previous Crop Yield</label>
                <input
                  type="number"
                  value={previousCropYield}
                  onChange={(e) => setPreviousCropYield(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Last season yield"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Program Level</label>
                <select
                  value={programLevel}
                  onChange={(e) => setProgramLevel(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="Excellent">Excellent</option>
                  <option value="Building">Building</option>
                  <option value="Minimal">Minimal</option>
                </select>
              </div>
            </div>

            {/* Organic Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="organic"
                checked={organic}
                onChange={(e) => setOrganic(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="organic" className="text-sm font-medium text-gray-700">
                Organic
              </label>
            </div>

            {/* Lime History */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Lime History</label>
                <button
                  onClick={addLimeEntry}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus size={16} />
                  Add Entry
                </button>
              </div>

              {limeHistory.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">No lime history</div>
              ) : (
                <div className="space-y-2">
                  {limeHistory.map((entry, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <select
                        value={entry.type}
                        onChange={(e) => updateLimeEntry(idx, 'type', e.target.value)}
                        className="border rounded px-2 py-1 text-sm flex-1"
                      >
                        <option>Calcium Carbonate</option>
                        <option>Dolomite</option>
                        <option>Gypsum</option>
                      </select>
                      <input
                        type="number"
                        value={entry.month}
                        onChange={(e) => updateLimeEntry(idx, 'month', parseInt(e.target.value))}
                        className="border rounded px-2 py-1 text-sm w-16"
                        min="1"
                        max="12"
                        placeholder="Mo"
                      />
                      <input
                        type="number"
                        value={entry.year}
                        onChange={(e) => updateLimeEntry(idx, 'year', parseInt(e.target.value))}
                        className="border rounded px-2 py-1 text-sm w-20"
                        placeholder="Year"
                      />
                      <input
                        type="number"
                        value={entry.amount_lbs_ac}
                        onChange={(e) => updateLimeEntry(idx, 'amount_lbs_ac', parseFloat(e.target.value))}
                        className="border rounded px-2 py-1 text-sm w-24"
                        placeholder="lbs/ac"
                      />
                      <button
                        onClick={() => removeLimeEntry(idx)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Special Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Special Notes</label>
              <textarea
                value={specialNotes}
                onChange={(e) => setSpecialNotes(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                rows="3"
                placeholder="Any special instructions or notes..."
              />
            </div>

            {/* Tests */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Tests</h3>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Standard (Always Included)</p>
                  <div className="grid grid-cols-4 gap-2 text-sm text-gray-500">
                    <div>✓ Boron (B)</div>
                    <div>✓ Calcium (Ca)</div>
                    <div>✓ Copper (Cu)</div>
                    <div>✓ Iron (Fe)</div>
                    <div>✓ Potassium (K)</div>
                    <div>✓ Magnesium (Mg)</div>
                    <div>✓ Manganese (Mn)</div>
                    <div>✓ Sodium (Na)</div>
                    <div>✓ Organic Matter (OM)</div>
                    <div>✓ Phosphorus (P2)</div>
                    <div>✓ pH (Water)</div>
                    <div>✓ Sulfur (S)</div>
                    <div>✓ Zinc (Zn)</div>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Optional Toggles</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'test_cl', label: 'Chloride (Cl)' },
                      { key: 'test_co', label: 'Cobalt (Co)' },
                      { key: 'test_mo', label: 'Molybdenum (Mo)' },
                      { key: 'test_salts', label: 'Salts' },
                      { key: 'test_bulk_den', label: 'Bulk Density' },
                      { key: 'test_olsen', label: 'Olsen P' },
                      { key: 'test_nh3', label: 'Ammonia (NH3)' },
                      { key: 'test_no3', label: 'Nitrate (NO3)' },
                      { key: 'test_ssc', label: 'SSC' }
                    ].map((test) => (
                      <label key={test.key} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={tests[test.key]}
                          onChange={() => toggleTest(test.key)}
                          className="mr-2"
                        />
                        {test.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Cancel
          </button>
          <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditSampleModal;
